import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { notify } from '../../shared/feedback/notify';
import EtapasTimeline from './EtapasTimeline';
import ModalSync from './ModalSync';
import { classeStatus, formatarDataHora, rotuloStatus } from './statusBadge';
import { JobCompleto } from './types';

interface Props {
  jobId: string;
  onClose: () => void;
  /**
   * Como buscar o detalhe. Parametrizado porque este modal serve painéis de backends
   * diferentes (Base de Dados e FIPE) — cada um passa a função do seu cliente de API.
   */
  fetchDetalhe: (jobId: string) => Promise<JobCompleto>;
  /** Prefixo da chave de cache, para os painéis não compartilharem entradas. */
  queryKeyBase: string;
  /** Título por tipo de job (ex: `bloco1` → "Bloco 1 — Referências SGA"). */
  titulosPorTipo?: Record<string, string>;
}

/**
 * Detalhe de uma execução: status, horários e as etapas — inclusive as re-tentadas
 * (aparecem repetidas de propósito; o histórico de tentativas é dado). Enquanto o job
 * está RUNNING, o refetch de 10s mantém a etapa atual viva dentro do modal.
 */
const JobDetalheModal: React.FC<Props> = ({ jobId, onClose, fetchDetalhe, queryKeyBase, titulosPorTipo }) => {
  const { data: job, error, isError, isLoading } = useQuery<JobCompleto>({
    queryKey: [queryKeyBase, 'job', jobId],
    queryFn: () => fetchDetalhe(jobId),
    refetchInterval: (query) => (query.state.data?.status === 'RUNNING' ? 10_000 : false),
  });

  /** Reprocessamento carrega a origem nos parâmetros, quando o backend a informa. */
  const tituloDoJob = (j: JobCompleto): string => {
    const base = titulosPorTipo?.[j.tipo] ?? j.tipo;
    if (j.tipo !== 'recover') return base;
    const origem = (j.resultado?.parametros as { origem?: string } | undefined)?.origem;
    return origem ? `${base} — ${origem}` : base;
  };

  useEffect(() => {
    if (isError) {
      notify.errorFrom(error, 'Não foi possível carregar o detalhe da execução. Feche o modal e tente de novo.', {
        toastId: `${queryKeyBase}-job-detalhe`,
      });
    }
  }, [isError, error, queryKeyBase]);

  return (
    <ModalSync
      titulo={job ? tituloDoJob(job) : 'Detalhe da execução'}
      subtitulo={job ? `Iniciada em ${formatarDataHora(job.startedAt)}` : undefined}
      onClose={onClose}
      // Tabela de 4 colunas: com os 560px do modal de filtros as colunas ficam cortadas.
      largura="min(920px, 94vw)"
    >
      {isLoading && <div className="admin-vazio">Carregando o detalhe da execução...</div>}

      {isError && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar o detalhe desta execução.
        </div>
      )}

      {job && (
        <>
          <div className="filtro-section">
            <h3 className="filtro-section-title">Resumo</h3>
            <div className="admin-linha-item">
              <span className="admin-texto-secundario">Situação</span>
              <span className={classeStatus(job.status)}>{rotuloStatus(job.status)}</span>
            </div>
            {job.finishedAt && (
              <div className="admin-linha-item">
                <span className="admin-texto-secundario">Fim</span>
                <span>{formatarDataHora(job.finishedAt)}</span>
              </div>
            )}
            {job.resultado?.mensagem && (
              <div className="admin-linha-item">
                <span className="admin-texto-secundario">Resultado</span>
                <span style={{ textAlign: 'right' }}>{job.resultado.mensagem}</span>
              </div>
            )}
          </div>

          <div className="filtro-section">
            <h3 className="filtro-section-title">Etapas</h3>
            <EtapasTimeline etapas={job.resultado?.etapas ?? []} />
          </div>
        </>
      )}
    </ModalSync>
  );
};

export default JobDetalheModal;
